import { format } from 'date-fns';

/**
 * Safely formats a date from Firestore
 * @param {any} dateValue - The date value from Firestore (could be Timestamp, Date, or string)
 * @param {string} formatString - The format string for date-fns
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Formatted date string
 */
export const safeFormatDate = (dateValue, formatString = 'MMM dd, yyyy', fallback = 'N/A') => {
  if (!dateValue) {
    return fallback;
  }

  try {
    let date;
    
    // Handle corrupted Firestore timestamp objects with _seconds and _nanoseconds
    if (dateValue && typeof dateValue === 'object' && dateValue._seconds !== undefined && dateValue._nanoseconds !== undefined) {
      console.warn('safeFormatDate: Found corrupted Firestore timestamp object:', dateValue);
      // Convert corrupted timestamp to Date
      date = new Date(dateValue._seconds * 1000 + dateValue._nanoseconds / 1000000);
    }
    // Handle proper Firestore Timestamp
    else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    }
    // Handle Date object
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    // Handle string or number
    else {
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return fallback;
    }

    return format(date, formatString);
  } catch (error) {
    console.warn('Error formatting date:', error, dateValue);
    return fallback;
  }
};

/**
 * Safely formats a date range
 * @param {any} startDate - Start date from Firestore
 * @param {any} endDate - End date from Firestore
 * @param {string} formatString - The format string for date-fns
 * @returns {string} Formatted date range string
 */
export const safeFormatDateRange = (startDate, endDate, formatString = 'MMM dd, yyyy') => {
  const start = safeFormatDate(startDate, formatString);
  const end = safeFormatDate(endDate, formatString);
  
  if (start === 'N/A' && end === 'N/A') {
    return 'N/A';
  }
  
  if (start === end) {
    return start;
  }
  
  return `${start} - ${end}`;
}; 

/**
 * Sanitize Firestore timestamps in message data
 * @param {Object|Array} data - The data to sanitize
 * @returns {Object|Array} Sanitized data with proper timestamp handling
 */
export const sanitizeTimestamps = (data) => {
  if (!data) return data;
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeTimestamps(item));
  }
  
  // Handle objects
  if (typeof data === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      if (key === 'timestamp' || key === 'createdAt' || key === 'updatedAt' || key === 'sentAt' || key === 'readAt' || key === 'date') {
        // Use safeFormatDate for timestamp fields, defaulting to ISO format
        sanitized[key] = safeFormatDate(value, 'yyyy-MM-dd HH:mm:ss', 'N/A');
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeTimestamps(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }
  
  return data;
};


export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};
